/* Module switcher in the top bar. Shows the active module and drops down the
 * rest, built modules above the ones that are only planned.
 *
 * It lists what this salon is licensed for and nothing else, and it is not here
 * at all when that comes to one module — see the note on `offered` and
 * `switchable` in apps.js for why, and for the argument this replaced.
 *
 * The whole component goes rather than the button being disabled or the menu
 * coming up empty. A control that opens to show you the screen you are already
 * on is worse than no control: it costs a press to learn nothing, and it costs
 * that press every time somebody new works the desk. */
import * as Vue from 'vue';
import { apps } from '../apps.js';

export const AppSwitcher = {
  name: 'AppSwitcher',
  setup: function () {
    var open = Vue.ref(false);

    function onKey(e) { if (e.key === 'Escape') open.value = false; }
    Vue.onMounted(function () { window.addEventListener('keydown', onKey); });
    Vue.onBeforeUnmount(function () { window.removeEventListener('keydown', onKey); });

    // A planned OR unlicensed module is switched to like any other: the stub
    // explains what it will be, or what it is not part of, which beats a menu
    // item that does nothing when clicked.
    // (This used to be an if/else with the same call in both arms.)
    function pick(app) {
      apps.setActive(app.id);
      open.value = false;
    }

    // Shut, the menu's counts can't be seen, so the button adds them up. It is the only
    // thing on the desk's screen that says the Optimizer has anything, and that is all
    // it does: no sound, nothing opens.
    var list = apps.list();
    var anyBadge = Vue.computed(function () {
      return list.value.reduce(function (n, app) { return n + (apps.badge(app) || 0); }, 0) || null;
    });

    return {
      open: open,
      anyBadge: anyBadge,
      list: list,
      switchable: apps.switchable,
      active: apps.active,
      badge: apps.badge,
      pick: pick
    };
  },
  template: [
    '<div v-if="switchable" class="switcher">',
    '  <button type="button" class="switcher__btn" :class="{ \'is-open\': open }"',
    '    aria-haspopup="menu" :aria-expanded="open ? \'true\' : \'false\'"',
    '    @click="open = !open">',
    '    <span class="switcher__icon" aria-hidden="true">{{ active ? active.icon : "▦" }}</span>',
    '    <span class="switcher__name">{{ active ? active.name : "Console" }}</span>',
    '    <span v-if="anyBadge" class="switcher__badge">{{ anyBadge }}</span>',
    '    <span class="switcher__caret" aria-hidden="true">▾</span>',
    '  </button>',
    '',
    '  <div v-if="open" class="switcher__scrim" @click="open = false"></div>',
    '  <div v-if="open" class="switcher__menu" role="menu">',
    '    <p class="switcher__label">Modules</p>',
    '    <button v-for="app in list" :key="app.id" type="button" class="switcher__item"',
    '      role="menuitem" :class="{ \'is-on\': active && app.id === active.id,',
    '                                 \'is-planned\': app.state !== \'ready\' }"',
    '      @click="pick(app)">',
    '      <span class="switcher__item-icon" aria-hidden="true">{{ app.icon }}</span>',
    '      <span class="switcher__item-main">',
    '        <span class="switcher__item-name">{{ app.name }}',
    '          <span v-if="badge(app)" class="switcher__badge">{{ badge(app) }}</span>',
    /* No "Not included" tag any more, and there cannot be one: this list is
       filtered to what the salon is offered, and `offered` is the exact
       negation of `locked`. A branch for it would be unreachable. */
    '          <span v-if="app.state === \'planned\'" class="switcher__tag">Planned</span>',
    '        </span>',
    '        <span class="switcher__item-blurb">{{ app.blurb }}</span>',
    '      </span>',
    '    </button>',
    '  </div>',
    '</div>'
  ].join('\n')
};
